import ConditionalRender from "../../../../reusable-components/ConditionalRender";
import digitalLabel from "../../../../assets/images/digital-icon-label.png";
import visibleEye from "../../../../assets/images/visible-eye.svg";
import hiddenEye from "../../../../assets/images/hidden-eye.svg";
import styles from "./BuildQuote.module.scss";
import { fieldNames } from "./constant";
import { TruckMaterialType } from "../../../../enums/truckMaterialType";
import { InputfieldTypes } from "../../../../enums/InputFieldAttributesEnums";
import { formatNumber } from "../../../../utils/numberFormatterUtil";

export const SUCCESS_CONTENT = "The quote has been sent to the customer";

export const SUCCESS_CONTENT_3 = "The estimate has been sent to the customer";

export const SUCCESS_CONTENT_2 =
  "New contact has been created and added to the database";

export const notificationConstants = {
  SEND_SELECT_ERROR: "Select at least one item to send",
  SELECT_ITEMS_TO_DOWNLOAD: "Select at least one item to download",
  SELECT_QUOTE_OR_ESTIMATE: "Select quote or estimate",
  CLICK_EDIT_TO_SAVE: "Click on edit button to save the contents",
  UPLOAD_SIGNATURE_OR_NAME: "Please upload signature or enter name",
  ENTER_QUOTE_PRINT_NAME: "Please enter the name to print on the quote",
  ENTER_QUOTE_TITLE: "Please enter the Title to print on the quote",
  ENTER_QUOTE_PHONE: "Please enter the phone number to print on the quote",
  ENTER_QUOTE_EMAIL: "Please enter the email to print on the quote",
  ENTER_QUOTE_DATE: "Please enter the date to print on the quote",
  EMAIL_MAX_LIMIT: "Email cannot exceed 50 characters",
  TITLE_MAX_LIMIT: "Title cannot exceed 50 characters",
  INVALID_EMAIL_FORMAT: "Please enter a valid email address",
  PRINT_NAME_ALPHANUMBERIC_FORMAT: "Print name should be only alphanumeric",
  TITLE_ALPHABET_FORMAT: "Title should contain only alphabets",
  PHONE_NUMBER_FORMAT: "Please enter a valid phone number",
  NO_CHANGES_TO_SAVE: "No changes to save.",
};
export enum DateFormat {
  DISPLAY = "DD/MM/YY",
  PARSE = "MM/DD/YYYY",
}

export const ValidationMessages = {
  DATE: {
    REQUIRED: "Please enter the date to print on the quote",
    INVALID_FORMAT: "Please enter a valid date in MM/DD/YYYY format",
    FUTURE_DATE: "Invalid input: Date cannot be in the future",
  },
  HEADERS: {
    REQUIRED: "required",
  },
};

export const ErrorTypes = {
  DATE: {
    REQUIRED: "required",
    FORMAT: "format",
    FUTURE: "future",
  },
  EMAIL: {
    LIMIT: "maxLength",
    INVALID: "validate",
    REQUIRED: "required",
  },
  HEADERS: {
    REQUIRED: "required",
  },
};

export const BuildQuoteFormFields = {
  QUOTE_DATE_FIELD: "quoteDate",
};

export const getColumns: ({
  handleSelectVendor,
  visiblePrices,
  togglePriceVisibility,
}: ColumnsProps & {
  visiblePrices: Record<
    string,
    { material: boolean; trucking: boolean; landed: boolean }
  >;
  togglePriceVisibility: (
    key: string,
    type: "material" | "trucking" | "landed"
  ) => void;
}) => ColumnsType<DataType> = ({ visiblePrices, togglePriceVisibility }) => {
  return [
    {
      title: "Pick Up",
      dataIndex: "origin",
      key: "origin",
      width: "200px",
      render: (text, record) => {
        return (
          <ConditionalRender visible={!!text} fallback={" - "}>
            {text}
          </ConditionalRender>
        );
      },
    },
    {
      title: "Destination",
      dataIndex: "destination",
      key: "destination",
      width: "200px",
      render: (text, record) => {
        return (
          <ConditionalRender visible={!!text} fallback={" - "}>
            {text}
          </ConditionalRender>
        );
      },
    },
    {
      title: "Material Name",
      dataIndex: "materialName",
      key: "materialName",
      width: "180px",
      render: (text, record) => {
        const value = record?.materialName ?? record?.materialTruckName;
        return (
          <ConditionalRender visible={!!value} fallback={" - "}>
            {value}
          </ConditionalRender>
        );
      },
    },
    {
      title: "Truck Type",
      dataIndex: "truckType",
      key: "truckType",
      width: "128px",
      render: (text) => (
        <>{text && text.join ? text.join(" , ") : text || "-"}</>
      ),
    },
    {
      title: "Quantity",
      dataIndex: "quantity",
      key: "quantity",
      width: "105px",
      render: (text, record) => {
        const quantity =
          record?.type === "material"
            ? record?.materialQuantity
            : record?.truckingQuantity;

        const unit =
          record?.type === "material"
            ? record?.materialQuantityUnitType
            : record?.truckingQuantityUnitType;

        return (
          <ConditionalRender visible={!!quantity} fallback={"-"}>
            {`${formatNumber(Number(quantity).toFixed(2))} ${unit || ""}`}
          </ConditionalRender>
        );
      },
    },
    {
      title: "Material Price",
      dataIndex: "materialPrice",
      key: "materialPrice",
      width: "132px",
      render: (text, record) => {
        const isVisible = visiblePrices[record?._key]?.material;
        return (
          <div className={styles.dataCell}>
            <span>
              {isVisible
                ? text !== null && text !== undefined && text > 0
                  ? `$${formatNumber(Number(text).toFixed(2))} / ${
                      record?.materialPriceType
                    }`
                  : "-"
                : "************"}
            </span>
            <img
              src={isVisible ? visibleEye : hiddenEye}
              onClick={() => togglePriceVisibility(record._key, "material")}
            />
          </div>
        );
      },
    },
    {
      title: "Trucking Price",
      dataIndex: "truckingPrice",
      key: "truckingPrice",
      width: "136px",
      render: (text, record) => {
        const isVisible = visiblePrices[record?._key]?.trucking;
        return (
          <div className={styles.dataCell}>
            <span>
              {isVisible
                ? text !== null && text !== undefined
                  ? `$ ${formatNumber(Number(text).toFixed(2))} / ${
                      record?.truckingPriceType
                    }`
                  : "-"
                : "************"}
            </span>
            <img
              src={isVisible ? visibleEye : hiddenEye}
              onClick={() => togglePriceVisibility(record._key, "trucking")}
            />
          </div>
        );
      },
    },
    {
      title: "Landed Price",
      dataIndex: "landedPrice",
      key: "landedPrice",
      width: "136px",
      render: (text, record) => {
        const isVisible = visiblePrices[record?._key]?.landed;
        return (
          <div className={styles.dataCell}>
            <span>
              {isVisible
                ? record?.type === TruckMaterialType.TRUCK_TYPE
                  ? record?.truckingPrice !== null &&
                    record?.truckingPrice !== undefined
                    ? `$ ${formatNumber(
                        Number(record?.truckingPrice).toFixed(2)
                      )} / ${record?.truckingPriceType}`
                    : "-"
                  : text !== null &&
                    text !== undefined &&
                    text !== "-" &&
                    record?.materialPriceType === record?.truckingPriceType
                  ? `$ ${Number(text).toFixed(2)} / ${record?.landedPriceType}`
                  : "-"
                : "************"}
            </span>
            <img
              src={isVisible ? visibleEye : hiddenEye}
              onClick={() => togglePriceVisibility(record._key, "landed")}
            />
          </div>
        );
      },
    },
  ];
};

export const SignatureTypeOptions = [
  { label: "Manual/Upload", value: "manual" },
  {
    label: (
      <>
        <img src={digitalLabel} alt="DIGITAL ICON" /> Digital
      </>
    ),
    value: "digital",
  },
];

export const quoteInputFields = {
  quoteDate: "quoteDate",
};

export const estimateValidatingFields = [
  fieldNames.ESTIMATE_COMPANY_NAME,
  fieldNames.ESTIMATE_COMPANY_ADDRESS,
  fieldNames.ESTIMATE_COMPANY_CONTACT,
  fieldNames.ESTIMATE_HEADER,
  fieldNames.ESTIMATE_AGGREGATE_SUPPLY_1,
  fieldNames.ESTIMATE_ADDRESS_HEADER,
  fieldNames.ESTIMATE_AGGREGATE_SUPPLY_2,
  fieldNames.ESTIMATE_PAYMENT_TERMS,
  fieldNames.ESTIMATE_THANK_YOU,
];
export const validatingFields = [
  fieldNames.TEMPLATE_COMPANY_NAME,
  fieldNames.TEMPLATE_COMPANY_ADDRESS,
  fieldNames.TEMPLATE_COMPANY_CONTACT,
  fieldNames.QUOTE_HEADER,
  fieldNames.TEMPLATE_ADDRESS_HEADER,
  fieldNames.QUOTE_TEXT_AREA_INTRO,
  fieldNames.QUOTE_PAYMENT_TERMS,
  fieldNames.QUOTE_SIGNATURE,
];

export const estimateNotificationMessages = {
  success: "Estimate saved successfully!",
  failure: "Unable to save estimate data",
};

export const headerEstimateTextAreaProps = [
  {
    textAreaRows: 1,
    showCount: true,
    maxLength: 45,
    type: InputfieldTypes.TEXT_AREA,
    name: fieldNames.ESTIMATE_COMPANY_NAME,
    className: styles.addressInputFields,
  },
  {
    textAreaRows: 1,
    showCount: true,
    maxLength: 90,
    type: InputfieldTypes.TEXT_AREA,
    name: fieldNames.ESTIMATE_COMPANY_ADDRESS,
    className: styles.addressInputFields,
  },
  {
    textAreaRows: 1,
    showCount: true,
    maxLength: 45,
    type: InputfieldTypes.TEXT_AREA,
    name: fieldNames.ESTIMATE_COMPANY_CONTACT,
    className: styles.addressInputFields,
  },
];

export const headerQuoteTextAreaProps = [
  {
    textAreaRows: 1,
    showCount: true,
    maxLength: 45,
    type: InputfieldTypes.TEXT_AREA,
    name: fieldNames.TEMPLATE_COMPANY_NAME,
    className: styles.addressInputFields,
  },
  {
    textAreaRows: 1,
    showCount: true,
    maxLength: 90,
    type: InputfieldTypes.TEXT_AREA,
    name: fieldNames.TEMPLATE_COMPANY_ADDRESS,
    className: styles.addressInputFields,
  },
  {
    textAreaRows: 1,
    showCount: true,
    maxLength: 45,
    type: InputfieldTypes.TEXT_AREA,
    name: fieldNames.TEMPLATE_COMPANY_CONTACT,
    className: styles.addressInputFields,
  },
];
